import { QueryClient, QueryObserverResult, useQuery, UseQueryOptions } from 'react-query'

import http from '../helpers/http'

import { HeaderSearchAssetType } from '../enums/enums'
import { ElasticSearchResult, HeaderSearchOption, ISearchUsersResults } from '../types/'

export const userSearchKey = 'user-search'
export const headerSearch = 'header-search'
export const prospectSearch = 'prospect-search'
export const organizationSearch = 'organization-search'

export function useHeaderSearchResults(
  query: string,
  key: HeaderSearchAssetType | null
): QueryObserverResult<HeaderSearchOption[]> {
  const groups = Object.values(HeaderSearchAssetType)
  const queryFn = ({ signal }: { signal?: AbortSignal }): Promise<HeaderSearchOption[]> =>
    http
      .get('elastic-search', {
        params: { search: query, assetType: key },
        signal,
      })
      .then((res) => {
        const groupedOptions = groups.map((group) => {
          const typeResult = group.concat('Result') as keyof ElasticSearchResult
          const data = res.data[typeResult]
          const editedOptions =
            Array.isArray(data) && data.length
              ? data.map((item) => {
                  return {
                    group: group,
                    ...item,
                  }
                })
              : []
          return [...editedOptions]
        })
        return groupedOptions.flat()
      })
  const config = { staleTime: Infinity, enabled: query.length > 1 }

  return useQuery([headerSearch, query, key], queryFn, config)
}

export function useUserSearchQuery(value: string): QueryObserverResult<ISearchUsersResults> {
  const queryFn = ({ signal }: { signal?: AbortSignal }): Promise<ISearchUsersResults> =>
    http
      .get(`/ll-users`, {
        params: { fullName: value },
        signal,
      })
      .then((res) => res?.data)
  const config: UseQueryOptions<ISearchUsersResults> = {
    queryKey: [userSearchKey, { value }],
    queryFn,
    enabled: value?.length > 0,
    staleTime: 0,
    notifyOnChangeProps: 'tracked',
  }

  return useQuery(config)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retry: 2,
    },
  },
})

export async function searchProspect({ email, fullName }: { email?: string; fullName?: string }) {
  return await queryClient.fetchQuery({
    queryKey: [prospectSearch, { searchedBy: email ?? fullName }],
    queryFn: ({ signal }) =>
      http.get('/collection/prospects', {
        params: {
          email: email ?? undefined,
          fullName: email ? undefined : fullName,
          disablePagination: true,
        },
        signal,
      }),
    staleTime: 0,
  })
}

export async function searchOrganization({ name }: { name: string }) {
  return await queryClient.fetchQuery({
    queryKey: [organizationSearch, { searchedBy: name }],
    queryFn: ({ signal }) =>
      http.get('/collection/organizations', {
        params: {
          name: name ?? undefined,
          disablePagination: true,
        },
        signal,
      }),
  })
}
